import { useEffect, useMemo } from 'react';

import { camelize } from '@/helpers/common';
import { useNotificationCenterSubscription } from '@/modules/common/hooks/useNotificationCenterSubscription';
import { RealTimeService } from '../helpers/RealTimeService';
import {
  CONNECTION_STATUS,
  useRealTimeStatus,
} from '../providers/RealTimeProvider';

const CHANNEL_NAMESPACE = 'ws';

export enum ChannelType {
  /** Casino-scoped channel limited to the current user: `ws:<client>:<name>#<user>` */
  Private = 'private',
  /** Casino-scoped channel open to everyone: `ws:<client>:<name>` */
  Public = 'public',
  /**
   * SOFTSWISS platform-level channel that is NOT namespaced under the casino,
   * e.g. analytics `$analytics#<user>`. The `$` prefix must stay at the very
   * start of the channel name.
   */
  Platform = 'platform',
}

const buildChannel = (
  channelName: string,
  clientName: string,
  user: string,
  channelType: ChannelType
): string => {
  switch (channelType) {
    case ChannelType.Platform:
      return `${channelName}#${user}`;
    case ChannelType.Public:
      return `${CHANNEL_NAMESPACE}:${clientName}:${channelName}`;
    default:
      return `${CHANNEL_NAMESPACE}:${clientName}:${channelName}#${user}`;
  }
};

export const useSubscription = <T>(
  channel: string | null,
  onMessage: (data: T) => void,
  channelType: ChannelType = ChannelType.Private
) => {
  const { status } = useRealTimeStatus();
  const { data: subscription } = useNotificationCenterSubscription();

  const subscriptionChannel = useMemo(() => {
    if (!(channel && subscription?.user && subscription?.clientName))
      return null;

    return buildChannel(
      channel,
      subscription.clientName,
      subscription.user,
      channelType
    );
  }, [channel, channelType, subscription?.user, subscription?.clientName]);

  useEffect(() => {
    if (!subscriptionChannel || status !== CONNECTION_STATUS.CONNECTED) return;

    const realTimeService = RealTimeService.getInstance();
    realTimeService.subscribe(subscriptionChannel, (data) => {
      onMessage(camelize(data as Record<string, unknown>) as T);
    });

    return () => {
      realTimeService.unsubscribe(subscriptionChannel);
    };
  }, [subscriptionChannel, status, onMessage]);
};
